import { Badge, Card, HStack, Progress, Text, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { KbStatsDto } from "types/knowledgeBase";
import { getThemeColor } from "theme/colors";
import { formatFileSize } from "utils/formatFileSize";

const NEARLY_FULL_PERCENT = 80;

const formatStorage = (bytes: number) =>
  bytes > 0 ? formatFileSize(bytes) : "0 MB";

/**
 * Knowledge-base storage used against the company's quota (uploaded documents + indexed web pages).
 * Uploads and crawls are refused once it is full.
 */
export default function KbStorageUsageCard({ stats }: { stats: KbStatsDto }) {
  const { t } = useTranslation();
  const percent =
    stats.storageQuotaBytes > 0
      ? Math.min(100, (stats.storageUsedBytes / stats.storageQuotaBytes) * 100)
      : 100;
  const isFull = stats.storageUsedBytes >= stats.storageQuotaBytes;
  const isNearlyFull = !isFull && percent >= NEARLY_FULL_PERCENT;
  const palette = isFull ? "red" : isNearlyFull ? "yellow" : "green";

  return (
    <Card.Root flex="1" minW="220px">
      <Card.Body>
        <VStack align="stretch" gap={1}>
          <HStack justify="space-between">
            <Text fontSize="sm" color={getThemeColor("text.secondary")}>
              {t("app.knowledgeBase.stats.storage.title")}
            </Text>
            {isNearlyFull && (
              <Badge colorPalette="yellow">
                {t("app.knowledgeBase.stats.storage.nearlyFull")}
              </Badge>
            )}
          </HStack>
          <Text fontSize="2xl" fontWeight="bold">
            {t("app.knowledgeBase.stats.storage.usedOf", {
              used: formatStorage(stats.storageUsedBytes),
              quota: formatStorage(stats.storageQuotaBytes),
            })}
          </Text>
          <Progress value={percent} size="sm" colorPalette={palette} />
          <Text fontSize="xs" color={getThemeColor("text.secondary")}>
            {t("app.knowledgeBase.stats.storage.breakdown", {
              documents: formatStorage(stats.documentsStorageBytes),
              webPages: formatStorage(stats.webPagesStorageBytes),
            })}
          </Text>
          {isFull && (
            <Text fontSize="xs" color="red.500">
              {t("app.knowledgeBase.stats.storage.full")}
            </Text>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}
